import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useAuth } from './AuthContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CartContext = createContext(null);

export const useCart = () => {
  const context = useContext(CartContext);
  if (!context) {
    throw new Error('useCart must be used within CartProvider');
  }
  return context;
};

const GUEST_CART_KEY = 'posterfire_guest_cart';
const SESSION_ID_KEY = 'posterfire_session_id';

const getSessionId = () => {
  let sid = localStorage.getItem(SESSION_ID_KEY);
  if (!sid) {
    sid = 'sess_' + Date.now() + '_' + Math.random().toString(36).substr(2, 9);
    localStorage.setItem(SESSION_ID_KEY, sid);
  }
  return sid;
};

const getGuestCart = () => {
  try {
    return JSON.parse(localStorage.getItem(GUEST_CART_KEY) || '[]');
  } catch { return []; }
};

const saveGuestCart = (items) => {
  localStorage.setItem(GUEST_CART_KEY, JSON.stringify(items));
};

const syncCartToServer = (items) => {
  try {
    const sessionId = getSessionId();
    const syncItems = items.map(i => ({
      product_id: i.product_id,
      title: i.product?.title || '',
      image_url: i.product?.image_url || '',
      size: i.size,
      quantity: i.quantity,
      price: i.price || 0
    }));
    axios.post(`${API}/cart/sync`, { session_id: sessionId, items: syncItems }).catch(() => {});
  } catch {}
};

export const CartProvider = ({ children }) => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const { token, isAuthenticated } = useAuth();

  const fetchCart = useCallback(async () => {
    if (!isAuthenticated) {
      setItems(getGuestCart());
      return;
    }
    try {
      setLoading(true);
      const response = await axios.get(`${API}/cart`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setItems(response.data);
    } catch (error) {
      console.error('Failed to fetch cart:', error);
    } finally {
      setLoading(false);
    }
  }, [token, isAuthenticated]);

  useEffect(() => {
    fetchCart();
  }, [fetchCart]);

  const addToCart = async (productId, quantity = 1, size = '18x24') => {
    if (isAuthenticated) {
      await axios.post(`${API}/cart`,
        { product_id: productId, quantity, size },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      await fetchCart();
      return;
    }
    // Guest cart - fetch product details and store locally
    try {
      const res = await axios.get(`${API}/products/${productId}`);
      const product = res.data;
      const guestItems = getGuestCart();
      const existing = guestItems.find(i => i.product_id === productId && i.size === size);
      // Get size-specific price
      let sizePrice = product.price;
      if (product.sizes?.length) {
        const sizeObj = product.sizes.find(s => s.size === size);
        if (sizeObj) sizePrice = sizeObj.price;
      }
      if (existing) {
        existing.quantity += quantity;
        existing.price = sizePrice;
      } else {
        guestItems.push({
          id: `guest_${Date.now()}`,
          product_id: productId,
          size,
          quantity,
          price: sizePrice,
          product: {
            id: product.id,
            title: product.title,
            image_url: product.image_url || product.imageUrl,
            price: product.price,
            sizes: product.sizes,
          }
        });
      }
      saveGuestCart(guestItems);
      setItems([...guestItems]);
      syncCartToServer(guestItems);
    } catch (error) {
      console.error('Failed to add to guest cart:', error);
      throw error;
    }
  };

  const updateQuantity = async (itemId, quantity) => {
    if (isAuthenticated) {
      await axios.put(`${API}/cart/${itemId}?quantity=${quantity}`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      await fetchCart();
      return;
    }
    const guestItems = getGuestCart();
    if (quantity <= 0) {
      const filtered = guestItems.filter(i => i.id !== itemId);
      saveGuestCart(filtered);
      setItems(filtered);
      syncCartToServer(filtered);
    } else {
      const item = guestItems.find(i => i.id === itemId);
      if (item) item.quantity = quantity;
      saveGuestCart(guestItems);
      setItems([...guestItems]);
      syncCartToServer(guestItems);
    }
  };

  const removeItem = async (itemId) => {
    if (isAuthenticated) {
      await axios.delete(`${API}/cart/${itemId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      await fetchCart();
      return;
    }
    const guestItems = getGuestCart().filter(i => i.id !== itemId);
    saveGuestCart(guestItems);
    setItems(guestItems);
    syncCartToServer(guestItems);
  };

  const clearCart = async () => {
    if (isAuthenticated) {
      await axios.delete(`${API}/cart`, {
        headers: { Authorization: `Bearer ${token}` }
      });
    }
    saveGuestCart([]);
    setItems([]);
    syncCartToServer([]);
  };

  const markConverted = () => {
    const sessionId = getSessionId();
    axios.post(`${API}/cart/mark-converted`, { session_id: sessionId }).catch(() => {});
  };

  const total = items.reduce((sum, item) => {
    const price = item.price || (item.product?.price || 0);
    return sum + (price * item.quantity);
  }, 0);

  const itemCount = items.reduce((sum, item) => sum + item.quantity, 0);

  const value = {
    items,
    loading,
    addToCart,
    updateQuantity,
    removeItem,
    clearCart,
    fetchCart,
    markConverted,
    total,
    itemCount
  };

  return (
    <CartContext.Provider value={value}>
      {children}
    </CartContext.Provider>
  );
};

export default CartContext;
